import type { Label, Node } from "@melo/shared";
import { store, useTables } from "../lib/store";
import { send } from "../lib/actions";
import { can } from "../lib/access";
import { MenuButton } from "./pickers";

export const LABEL_COLORS = ["#0071bc", "#449ae5", "#2f6f7a", "#3f7a3a", "#6d5f2f", "#8a4a2f", "#a33a3a", "#a0507a", "#5b3f7a", "#5b6577"];

export function labelsOf(spaceId: string): Label[] {
  return [...store.t.labels.values()].filter((l) => l.spaceId === spaceId).sort((a, b) => (a.rank < b.rank ? -1 : 1));
}

export function LabelChip({ label }: { label: Label }) {
  return (
    <span className="chip label-chip">
      <span className="d" style={{ background: label.color }} />
      {label.name}
    </span>
  );
}

export function LabelChips({ ids, max = 3 }: { ids: string[]; max?: number }) {
  const t = useTables();
  const list = ids.map((id) => t.labels.get(id)).filter((l): l is Label => Boolean(l));
  if (!list.length) return null;
  return (
    <span className="label-chips">
      {list.slice(0, max).map((l) => (
        <LabelChip key={l.id} label={l} />
      ))}
      {list.length > max && <span className="chip">+{list.length - max}</span>}
    </span>
  );
}

/** Creates a label with the next color in the palette. */
export async function createLabel(spaceId: string, name: string): Promise<Label> {
  const used = labelsOf(spaceId).length;
  return (await send("label.upsert", { spaceId, name: name.trim(), color: LABEL_COLORS[used % LABEL_COLORS.length]! })) as Label;
}

/**
 * Picks labels for a set of selected ids. `onChange` gets the full new list. Typing a name that does not exist
 * offers to create it (members and producers).
 */
export function LabelPicker({ spaceId, value, onChange, className = "chip", children }: { spaceId: string; value: string[]; onChange: (ids: string[]) => void; className?: string; children?: React.ReactNode }) {
  const t = useTables();
  const labels = [...t.labels.values()].filter((l) => l.spaceId === spaceId).sort((a, b) => (a.rank < b.rank ? -1 : 1));
  return (
    <MenuButton
      label="Labels"
      className={className}
      searchable
      multi
      placeholder="Find or create a label"
      createLabel="Create label"
      onCreate={can(spaceId, "member") ? (name) => void createLabel(spaceId, name).then((l) => onChange([...value, l.id])) : undefined}
      items={() => [
        ...labels.map((l) => ({
          id: l.id,
          label: l.name,
          icon: <span className="label-dot" style={{ background: l.color }} />,
          checked: value.includes(l.id),
          run: () => onChange(value.includes(l.id) ? value.filter((x) => x !== l.id) : [...value, l.id]),
        })),
      ]}
    >
      {children ?? (value.length ? <LabelChips ids={value} /> : <span className="muted">Add label</span>)}
    </MenuButton>
  );
}

/** Label editing on an item, used by the item panel. */
export function NodeLabels({ node, editable }: { node: Node; editable: boolean }) {
  if (!editable) return node.labels.length ? <LabelChips ids={node.labels} max={10} /> : <span className="muted">None</span>;
  return <LabelPicker spaceId={node.spaceId} value={node.labels} onChange={(labels) => void send("node.update", { nodeId: node.id, labels }, [{ table: "nodes", id: node.id, value: { ...node, labels } }])} />;
}
